/**
 * Thin client logger (FE-04).
 *
 * Two layers keep console noise out of production:
 *
 *  1. BUILD layer — `next.config.js` sets
 *     `compiler.removeConsole: { exclude: ['error'] }` for production builds,
 *     so every `console.log/info/warn/debug` (and the `logger.*` calls below,
 *     which compile down to those) is physically removed from the prod bundle.
 *     `console.error` is intentionally kept for runtime error visibility.
 *
 *  2. RUNTIME layer — this module also gates by level, so even in a
 *     non-production build you can quiet things down with
 *     `NEXT_PUBLIC_LOG_LEVEL` (debug | info | warn | error | silent).
 *
 * The existing ~600 raw `console.*` calls are already covered by layer 1 and
 * do NOT need a risky mass-rewrite. Use `logger` for NEW code so we get a
 * single, levelled entry point. `logger.error` also reports to the host's
 * log pipeline (see client-errors.ts) — never to a third party.
 *
 *   import { logger } from '@/lib/logger';
 *   logger.debug('ws message', payload);   // stripped in prod
 *   logger.error('save failed', err);      // kept in prod
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 100,
};

function resolveLevel(): LogLevel {
  const env = (process.env.NEXT_PUBLIC_LOG_LEVEL || '').toLowerCase();
  if (env in LEVEL_ORDER) return env as LogLevel;
  // Default: noisy in dev, quiet (warn+) in production. Note that in prod the
  // build layer has already stripped debug/info/warn anyway — this is the
  // belt-and-suspenders for non-prod NODE_ENVs and SSR.
  return process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
}

const threshold = LEVEL_ORDER[resolveLevel()];

function enabled(level: Exclude<LogLevel, 'silent'>): boolean {
  return LEVEL_ORDER[level] >= threshold;
}

import { reportClientError } from '@/lib/client-errors';

export const logger = {
  debug: (...args: unknown[]): void => {
    if (enabled('debug')) console.debug(...args);
  },
  info: (...args: unknown[]): void => {
    if (enabled('info')) console.info(...args);
  },
  warn: (...args: unknown[]): void => {
    if (enabled('warn')) console.warn(...args);
  },
  error: (...args: unknown[]): void => {
    // Always allowed through the runtime gate; the build layer keeps
    // console.error in production on purpose.
    if (enabled('error')) console.error(...args);
    // And to the host's log pipeline: the console is not monitored. The
    // first Error in the arguments is the report; a plain message becomes one.
    const err = args.find((a) => a instanceof Error) ?? args.map(String).join(' ');
    void reportClientError(err, { source: 'logger' });
  },
};

export type { LogLevel };
